/* eslint-disable jsx-a11y/anchor-is-valid */
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";

import {
  faArrowRightLong,
  faChevronDown,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy, { useSingleton } from "@tippyjs/react";
import classNames from "classnames";
import { Link } from "gatsby";
import React, { useLayoutEffect } from "react";
import { useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import styled, { createGlobalStyle } from "styled-components";
import tw, { css } from "twin.macro";

import headerWaveMobileSrc from "../../assets/images/header-wave-mobile.svg";
import Button from "../common/Button";
import HeaderLogo from "./HeaderLogo";
import {
  defaultMenuWidth,
  IntegrationIcon,
  MobileIntegration,
  resourcesMenuWidth,
} from "./nav/MenuStyles";
import { features, forIndustries, forTeams } from "./nav/navItems";
import ProductMenu from "./nav/ProductMenu";
import ResourcesMenu from "./nav/ResourcesMenu";
import {
  ConvertLink,
  CustomerPortalLink,
  ReactivateLink,
  RetainLink,
} from "./nav/SolutionLink";
import { LogoColor } from "./partials/Logo";

const Nav = styled.div`
  ${tw`relative h-full flex justify-between items-center !font-title font-normal text-theme-gray-7`}
  font-size: 16px;
`;

const DesktopNav = styled.nav`
  ${tw`justify-end`}

  @media (max-width: 950px) {
    li:not(.hamburger-menu):not(.logo) {
      display: none !important;
    }
  }

  .hamburger-menu {
    ${tw`hidden`}
    z-index: 9999;

    @media (max-width: 950px) {
      ${tw`flex`}
    }

    &.is-active {
      animation: hamburger-up 250ms ease-in-out forwards;

      &.hamburger-menu-is-blog-nav {
        animation-name: hamburger-up-blog-nav;
      }
    }

    &:not(.is-active).has-opened {
      animation: hamburger-down 250ms ease-in-out forwards;

      &.hamburger-menu-is-blog-nav {
        animation-name: hamburger-down-blog-nav;
      }
    }

    @keyframes hamburger-up {
      0% {
        transform: translate(0%, 0%);
      }
      100% {
        transform: translate(10px, -30px);
      }
    }

    @keyframes hamburger-down {
      0% {
        transform: translate(10px, -30px);
      }
      100% {
        transform: translate(0%, 0%);
      }
    }

    @keyframes hamburger-up-blog-nav {
      0% {
        transform: translate(0%, 0%);
      }
      100% {
        transform: translate(-3px, -17px);
      }
    }

    @keyframes hamburger-down-blog-nav {
      0% {
        transform: translate(-3px, -17px);
      }
      100% {
        transform: translate(0%, 0%);
      }
    }
  }
`;

const MobileNav = styled.nav`
  ${tw`fixed flex flex-col bg-white z-50 overflow-auto overscroll-none font-title text-theme-gray-7`}

  border-radius: 20px 0px 0px 20px;
  box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.1);

  top: 0;
  right: 0;

  width: 480px;
  max-width: 95vw;
  min-width: 250px;

  height: 100%;

  transform: translateX(100%);

  transition: opacity 250ms ease-in-out;

  &.is-active {
    animation: slide-in 250ms ease-in-out forwards;
    opacity: 1;
  }

  &:not(.is-active) {
    animation: slide-out 250ms ease-in-out forwards;
    opacity: 0;
  }

  @keyframes slide-in {
    100% {
      transform: translateX(0%);
    }
  }

  @keyframes slide-out {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const GlobalStyle = createGlobalStyle`
  .tippy-box.nav-menu {
    position: relative;
    top: -1px;
    border-radius: 20px !important;
    box-shadow: none !important;
  }

  .tippy-box.nav-menu .tippy-content {
    margin-top: 10px;
    padding: 0;
    border-radius: 20px !important;
    box-shadow: none !important;
  }
  .tippy-box,
  .tippy-content,
  .tippy-content > div {
    &:focus {
      outline: none;
    }
  }
`;

const DesktopMenuBar = styled.ul`
  ${tw`gap-0 flex flex-row flex-grow items-stretch my-auto px-0`}

  transition: box-shadow 250ms ease-in-out;

  .logo {
    ${tw`self-center`}
  }

  a {
    &:hover {
      text-decoration: none !important;
    }
  }

  li {
    ${tw`!my-0`}
  }
`;

const MobileMenuBar = styled.ul`
  ${tw`sticky top-0 flex flex-row gap-4 pl-5 !my-0 z-50 bg-white list-none`}

  border-radius: 20px 0px 0px 0px;
  max-height: 60px !important;

  box-shadow: none;
  transition: box-shadow 250ms ease-in-out;

  li {
    ${tw`!my-0`}
  }

  &.is-scrolled {
    box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.07);
  }
`;

const MobileMenuContentWrapper = tw.div`flex-grow`;

const DesktopMenu = styled.li`
  ${tw`flex flex-col items-center justify-center`}
  border-radius: 6px;
  min-height: 60px;

  &.active {
    background-color: #eef2f5;
  }

  &.no-menu {
    a {
      ${tw`text-[#5D5D68] font-normal`}
    }
    :hover {
      background-color: #eef2f5;
    }
  }
`;

const MobileMenu = styled.li`
  ${tw`px-0 py-3`}

  @media (min-width: 951px) {
    display: none;
  }
`;

const DesktopDropdownTrigger = styled.button`
  ${tw`flex-grow flex items-center gap-1 border-none bg-transparent font-title text-[#5D5D68]`}
  cursor: default;
  padding: 0 24px;

  &.active {
    background-color: #f1f7ff;
    border-radius: 6px;
  }
`;

const DesktopTrigger = styled.div`
  ${tw`flex-grow flex items-center px-5`}
  cursor: pointer;

  &:hover {
    background-color: #f1f7ff;
    border-radius: 6px;
  }
`;

const MobileMenuTrigger = styled((props) => <Link {...props} />)`
  ${tw`!text-theme-gray-7 !font-normal !pt-0`}

  font-size: 18px;
  line-height: 22px;

  &:hover {
    color: #232e3b !important;
    text-decoration: none !important;
  }

  &:active,
  &.active {
    ${tw`!text-theme-blue-1 !font-semibold`}
    text-decoration: none !important;

    &:after {
      ${tw`bg-theme-blue-1`}
      content: "";
      float: left;
      width: 100%;
      height: 3px;
      border-radius: 30px;
      margin-top: 0px;
    }
  }
`;

const MobileMenuContent = styled.div`
  ${tw`relative flex flex-col !flex-grow mb-0 w-full h-full bg-white`};

  a {
    ${tw`text-theme-gray-7`}
  }
`;

const MobileMenuSection = styled.div`
  ${tw`relative flex flex-col gap-0 m-0 px-4 py-2`}
`;

const MobileMenuSectionWave = styled(MobileMenuSection)`
  ${tw`mt-0 pt-10 !flex-grow`}
  background: linear-gradient(90deg, #f1f4f7 3.32%, #f1f4f7 198.67%);
  margin-bottom: auto !important;

  &:after {
    content: "";
    position: absolute;
    background-image: url(${headerWaveMobileSrc});
    width: 100% !important;
    height: 20px;
    top: 0px;
    left: 0px;
  }
`;

const MobileMenuSectionGrid = styled.div`
  ${tw`grid grid-flow-row grid-cols-2 auto-cols-fr items-center place-content-start gap-5 pt-4 pb-3 max-w-[350px]`}

  @media (max-width: 350px) {
    ${tw`grid-cols-1`}
  }
`;

const MobileMenuSectionSolutions = styled.div`
  ${tw`flex flex-col gap-3 pt-3`}
`;

const MobileMenuFooter = styled.div`
  ${tw`sticky bottom-0 flex flex-col justify-end mb-0 self-stretch p-4 pt-0 self-start`};

  background: linear-gradient(90deg, #f1f4f7 3.32%, #f1f4f7 198.67%);
  border-radius: 0px 0px 0px 20px;
  transition: box-shadow 250ms ease-in-out;

  &:not(.is-bottom-scrolled).is-scrollable,
  &:not(.is-bottom-scrolled).is-scrolled {
    box-shadow: 0px -10px 40px rgba(0, 0, 0, 0.07);
  }

  &:not(.is-scrollable) {
    &:before {
      ${tw`absolute top-0 left-0 w-full`}
      content: "";
      height: 1px;
      border-top: solid 1px #e0e0e0;
    }
  }
`;

const MobileMenuButtonGroup = styled.div`
  ${tw`flex flex-row flex-nowrap gap-3 justify-start pt-5 pb-0`}

  button, a {
    ${tw`!mx-0 flex-grow`}
    padding-left: unset;
    padding-right: unset;

    &.get-a-demo {
      ${tw`basis-2/3`}
    }

    &:not(.get-a-demo) {
      ${tw`basis-1/3`}
    }
  }
`;

const MobileMenuSectionContent = styled.div`
  ${tw`relative pl-0 py-0`}

  &:not(:first-child) {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: solid 1px #f2f2f2;
  }
`;

const MobileMenuSectionTitle = styled.div`
  ${tw`font-semibold`}
  font-size: 16px;
  line-height: 16px;
  color: #232e3b;
`;

const MobileMenuItem = styled((props) => <Link {...props} />)`
  ${tw`flex !px-0 !py-0 !m-0 cursor-pointer font-subtitle`}
  font-size: 16px;
  line-height: 22px;

  text-transform: none !important;
  font-weight: normal !important;

  &:hover {
    color: #232e3b !important;
    text-decoration: none !important;
  }

  &:last-of-type {
    ${tw`!pb-0`}
  }
`;

const MobileMenuItemAnchor = styled.a`
  ${tw`flex px-0! py-0! m-0! cursor-pointer font-subtitle`}
  font-size: 16px;
  line-height: 22px;

  text-transform: none !important;
  font-weight: normal !important;

  &:hover {
    color: #232e3b !important;
    text-decoration: none !important;
  }
`;

const BlueMobileMenuItem = styled(MobileMenuItem)`
  ${tw`!font-title !text-theme-blue-1 !font-semibold`}

  &:hover {
    color: #004ce2 !important;
    text-decoration: none !important;
  }
`;

interface HeaderNavProps {
  logoWhiteAtTop?: boolean;
  showGetADemo?: boolean;
  isBlogNav?: boolean; // Hack to adjust hamburger open position in blog nav
}

const HeaderNav: React.FunctionComponent<HeaderNavProps> = ({
  logoWhiteAtTop = true,
  showGetADemo = true,
  isBlogNav = false,
}) => {
  const [isActive, setIsActive] = useState(false);
  const [menuIsScrolled, setMenuIsScrolled] = useState(false);
  const [menuIsScrollable, setMenuIsScrollable] = useState(false);
  const [menuIsBottomScrolled, setMenuIsBottomScrolled] = useState(false);
  const tippyInstancesRef = useRef<any[]>([]);
  const [productMenuIsOpen, setProductMenuIsOpen] = useState(false);
  const [forTeamsMenuIsOpen, setForTeamsMenuIsOpen] = useState(false);
  const [mobileProductMenuIsOpen, setMobileProductMenuIsOpen] = useState(false);
  const [mobileResourcesMenuIsOpen, setMobileResourcesMenuIsOpen] =
    useState(false);
  const [hamburgerClicked, setHamburgerClicked] = useState(false);

  const mobileNavRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const [source, target] = useSingleton();

  const sinkHubSpotChat = () => {
    const chat = document.getElementById("hubspot-messages-iframe-container");
    if (chat) {
      chat.style.zIndex = "0";
    }
  };

  const floatHubSpotChat = () => {
    const chat = document.getElementById("hubspot-messages-iframe-container");
    if (chat) {
      chat.style.zIndex = "2147483647";
    }
  };

  const openMenu = () => {
    if (!mobileNavRef.current) {
      return;
    }
    document.body.classList.add("off-nav-is-active");
    sinkHubSpotChat();
    if (!mobileProductMenuIsOpen && !mobileResourcesMenuIsOpen) {
      setMobileProductMenuIsOpen(!mobileResourcesMenuIsOpen);
    }
    setIsActive(true);
  };

  const closeMenu = () => {
    document.body.classList.remove("off-nav-is-active");
    setIsActive(false);
    floatHubSpotChat();
    setTimeout(() => {
      setMobileProductMenuIsOpen(false);
      setMobileResourcesMenuIsOpen(false);
    }, 300);
  };

  useLayoutEffect(() => {
    if (!mobileNavRef.current) {
      return;
    }
    mobileNavRef.current.scrollTo({ top: 0 });
    setMenuIsScrolled(false);
    setMenuIsBottomScrolled(false);
    setMenuIsScrollable(
      mobileNavRef.current.scrollHeight > mobileNavRef.current.clientHeight
    );
  }, [mobileProductMenuIsOpen, mobileResourcesMenuIsOpen]);

  const hideTippy = () => {
    source.data?.instance.hide();
  };

  useHotkeys("Escape", () => {
    if (isActive) {
      closeMenu();
    }

    hideTippy();
  });

  const clickOutside = (e: MouseEvent) => {
    if (!mobileNavRef.current) {
      return;
    }

    if (
      !mobileNavRef.current.classList.contains("is-active") ||
      (mobileNavRef.current.contains(e.target as Node) &&
        (!(e.target instanceof HTMLAnchorElement) ||
          (e.target as HTMLAnchorElement).href !== window.location.href)) ||
      e.target === hamburgerRef.current
    ) {
      return;
    }

    closeMenu();
  };

  const menuScroll = () => {
    if (mobileNavRef.current) {
      setMenuIsScrolled(mobileNavRef.current.scrollTop > 0);
      setMenuIsBottomScrolled(
        mobileNavRef.current.scrollHeight - mobileNavRef.current.scrollTop <=
          mobileNavRef.current.clientHeight
      );
    }
  };

  const resize = () => {
    if (window.innerWidth >= 950) {
      closeMenu();
      setHamburgerClicked(false);
    }
    if (mobileNavRef.current) {
      setMenuIsScrollable(
        mobileNavRef.current.scrollHeight > mobileNavRef.current.clientHeight
      );
    }
  };

  useLayoutEffect(() => {
    if (mobileNavRef) {
      document.addEventListener("click", clickOutside);
      window.addEventListener("resize", resize);
    }

    return () => {
      document.removeEventListener("click", clickOutside);
      window.removeEventListener("resize", resize);

      closeMenu();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const currentNavRef = mobileNavRef.current;
    if (!currentNavRef) {
      return;
    }

    currentNavRef.addEventListener("scroll", menuScroll);
    return () => {
      currentNavRef.removeEventListener("scroll", menuScroll);
    };
  }, []);

  return (
    <>
      <GlobalStyle />
      <Nav>
        <HeaderLogo
          color={logoWhiteAtTop ? LogoColor.WhiteAtTop : LogoColor.Default}
        />
        <DesktopNav className={classNames(isActive && "is-active")}>
          <DesktopMenuBar
            className={classNames(
              isActive && "is-active",
              menuIsScrolled && "is-scrolled"
            )}
          >
            <Tippy
              theme="light"
              className="nav-menu"
              singleton={source}
              interactive={true}
              arrow={false}
              placement="bottom"
              popperOptions={{
                placement: "bottom",
                strategy: "absolute",
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      altAxis: true,
                    },
                  },
                ],
              }}
              hideOnClick={false}
              onTrigger={(instance, event) => {
                const tippyInstance = tippyInstancesRef.current.find(
                  (i) => i.reference === event.currentTarget
                );
                if (tippyInstance.props.className.includes("product-menu")) {
                  setProductMenuIsOpen(true);
                  setForTeamsMenuIsOpen(false);
                } else if (
                  tippyInstance.props.className.includes("for-teams-menu")
                ) {
                  setForTeamsMenuIsOpen(true);
                  setProductMenuIsOpen(false);
                }
              }}
              onHide={() => {
                setProductMenuIsOpen(false);
                setForTeamsMenuIsOpen(false);
              }}
            />
            <DesktopMenu>
              <Tippy
                theme="light"
                className="nav-menu product-menu"
                singleton={target}
                interactive={true}
                onCreate={(instance) => {
                  tippyInstancesRef.current.push(instance);
                }}
                maxWidth={defaultMenuWidth}
                content={<ProductMenu />}
              >
                <DesktopDropdownTrigger
                  className={productMenuIsOpen ? "active" : undefined}
                >
                  <span>
                    Product{" "}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      transform="shrink-3"
                    />
                  </span>
                </DesktopDropdownTrigger>
              </Tippy>
            </DesktopMenu>
            <DesktopMenu>
              <Tippy
                theme="light"
                className="nav-menu for-teams-menu"
                singleton={target}
                interactive={true}
                onCreate={(instance) => {
                  tippyInstancesRef.current.push(instance);
                }}
                maxWidth={resourcesMenuWidth}
                content={<ResourcesMenu />}
              >
                <DesktopDropdownTrigger
                  className={forTeamsMenuIsOpen ? "active" : undefined}
                >
                  <span>
                    Resources{" "}
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      transform="shrink-3"
                    />
                  </span>
                </DesktopDropdownTrigger>
              </Tippy>
            </DesktopMenu>
            <DesktopMenu className="no-menu">
              <Link tw="flex-grow !flex items-stretch" to="/pricing/">
                <DesktopTrigger>Pricing</DesktopTrigger>
              </Link>
            </DesktopMenu>
            <DesktopMenu className="no-menu">
              <a
                tw="flex-grow !flex items-stretch"
                href="https://app.prosperstack.com"
              >
                <DesktopTrigger>Log in</DesktopTrigger>
              </a>
            </DesktopMenu>
            {showGetADemo && (
              <DesktopMenu
                css={css`
                  ${tw`ml-2`}
                  a {
                    @media (max-width: 1000px) {
                      max-width: 180px;
                    }
                    @media (max-width: 990px) {
                      max-width: 160px;
                    }
                    @media (max-width: 980px) {
                      max-width: 150px;
                    }
                  }
                `}
              >
                <Button
                  className="button button-primary button-wide-mobile get-a-demo"
                  tag="a"
                  href="#get-a-demo"
                >
                  Get a demo
                </Button>
              </DesktopMenu>
            )}
            <DesktopMenu
              className={classNames(
                "hamburger-menu",
                isActive && "is-active",
                hamburgerClicked && "has-opened",
                isBlogNav && "hamburger-menu-is-blog-nav"
              )}
            >
              <button
                className="header-nav-toggle"
                ref={hamburgerRef}
                onClick={() => {
                  setHamburgerClicked(true);
                  isActive ? closeMenu() : openMenu();
                }}
              >
                <span className="screen-reader">Menu</span>
                <span className="hamburger">
                  <span className="hamburger-inner"></span>
                </span>
              </button>
            </DesktopMenu>
          </DesktopMenuBar>
        </DesktopNav>
        <MobileNav
          className={classNames(
            menuIsScrolled && "is-scrolled",
            menuIsScrollable && "is-scrollable",
            isActive ? "is-active" : "is-inactive"
          )}
          ref={mobileNavRef}
        >
          <MobileMenuBar
            className={classNames(menuIsScrolled && "is-scrolled")}
          >
            <MobileMenu>
              <MobileMenuTrigger
                to="#"
                className={mobileProductMenuIsOpen ? "active" : undefined}
                onClick={(e: MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMobileProductMenuIsOpen(true);
                  setMobileResourcesMenuIsOpen(false);
                  window.setTimeout(() => {
                    openMenu();
                  }, 201);
                }}
              >
                Product
              </MobileMenuTrigger>
            </MobileMenu>
            <MobileMenu>
              <MobileMenuTrigger
                to="#"
                className={mobileResourcesMenuIsOpen ? "active" : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMobileResourcesMenuIsOpen(true);
                  setMobileProductMenuIsOpen(false);
                  openMenu();
                }}
              >
                Resources
              </MobileMenuTrigger>
            </MobileMenu>
            <MobileMenu>
              <MobileMenuTrigger
                to="/pricing/"
                onClick={() => {
                  setMobileResourcesMenuIsOpen(false);
                  setMobileProductMenuIsOpen(false);
                }}
              >
                Pricing
              </MobileMenuTrigger>
            </MobileMenu>
          </MobileMenuBar>
          <MobileMenuContentWrapper>
            <MobileMenuContent
              css={css`
                display: ${mobileProductMenuIsOpen
                  ? "visible"
                  : "none"} !important;
              `}
            >
              <MobileMenuSection>
                <MobileMenuSectionContent>
                  <MobileMenuSectionTitle>Solutions</MobileMenuSectionTitle>
                  <MobileMenuSectionSolutions>
                    <RetainLink />
                    <ConvertLink />
                    <ReactivateLink />
                    <CustomerPortalLink />
                  </MobileMenuSectionSolutions>
                </MobileMenuSectionContent>
                <MobileMenuSectionContent>
                  <MobileMenuSectionTitle>Features</MobileMenuSectionTitle>
                  <MobileMenuSectionGrid tw="grid-flow-row">
                    {features.map((link) => (
                      <MobileMenuItem to={link.to} key={link.to}>
                        {link.icon && <>{link.icon}&nbsp;</>}
                        {link.title}
                      </MobileMenuItem>
                    ))}
                  </MobileMenuSectionGrid>
                </MobileMenuSectionContent>
              </MobileMenuSection>
            </MobileMenuContent>
            <MobileMenuContent
              css={css`
                display: ${mobileResourcesMenuIsOpen
                  ? "visible"
                  : "none"} !important;
              `}
            >
              <MobileMenuSection>
                <MobileMenuSectionContent>
                  <MobileMenuSectionTitle>Learn more</MobileMenuSectionTitle>
                  <MobileMenuSectionGrid tw="grid-flow-row">
                    <MobileMenuItemAnchor
                      href="https://docs.prosperstack.com"
                      target="_blank"
                    >
                      Documentation
                    </MobileMenuItemAnchor>
                    <MobileMenuItem to="/blog/">Blog</MobileMenuItem>
                    <MobileMenuItemAnchor
                      href="https://developers.prosperstack.com"
                      target="_blank"
                    >
                      API Reference
                    </MobileMenuItemAnchor>
                    <MobileMenuItem to="/roi-calculator/">
                      ROI Calculator
                    </MobileMenuItem>
                    <MobileMenuItemAnchor
                      href="https://changelog.prosperstack.com"
                      target="_blank"
                    >
                      Product Updates
                    </MobileMenuItemAnchor>
                  </MobileMenuSectionGrid>
                </MobileMenuSectionContent>
                <MobileMenuSectionContent>
                  <MobileMenuSectionTitle>For teams</MobileMenuSectionTitle>
                  <MobileMenuSectionGrid tw="grid-flow-row">
                    {forTeams.map((link) => (
                      <MobileMenuItem to={link.to} key={link.to}>
                        {link.title}
                      </MobileMenuItem>
                    ))}
                  </MobileMenuSectionGrid>
                </MobileMenuSectionContent>
                <MobileMenuSectionContent>
                  <MobileMenuSectionTitle>
                    For industries
                  </MobileMenuSectionTitle>
                  <MobileMenuSectionGrid tw="grid-flow-row">
                    {forIndustries.map((link) => (
                      <MobileMenuItem to={link.to} key={link.to}>
                        {link.title}
                      </MobileMenuItem>
                    ))}
                  </MobileMenuSectionGrid>
                </MobileMenuSectionContent>
              </MobileMenuSection>

              <MobileMenuSectionWave tw="pb-4">
                <MobileMenuSectionContent>
                  <MobileMenuSectionTitle>Integrations</MobileMenuSectionTitle>
                  <MobileMenuSectionGrid>
                    <MobileIntegration to="/integrations/stripe/">
                      <IntegrationIcon color="#635BFF">
                        <img
                          src={
                            require("../../assets/images/integrations/logos/stripe-logo-white.svg")
                              .default
                          }
                          width="22px"
                          alt="Stripe"
                        />
                      </IntegrationIcon>{" "}
                      Stripe
                    </MobileIntegration>
                    <MobileIntegration to="/integrations/chargebee/">
                      <IntegrationIcon color="#FF3300">
                        <img
                          src={
                            require("../../assets/images/integrations/logos/chargebee-mark-white.svg")
                              .default
                          }
                          width="18px"
                          alt="Chargebee"
                        />
                      </IntegrationIcon>{" "}
                      Chargebee
                    </MobileIntegration>

                    <MobileIntegration to="/integrations/recurly/">
                      <IntegrationIcon color="#FFD706">
                        <img
                          src={
                            require("../../assets/images/integrations/logos/recurly-mark-white.svg")
                              .default
                          }
                          width="18px"
                          alt="Recurly"
                        />
                      </IntegrationIcon>{" "}
                      Recurly
                    </MobileIntegration>

                    <MobileIntegration to="/integrations/salesforce/">
                      <IntegrationIcon color="#00A1DF">
                        <img
                          src={
                            require("../../assets/images/integrations/logos/salesforce-logo-white.svg")
                              .default
                          }
                          width="20px"
                          alt="Salesforce"
                        />
                      </IntegrationIcon>{" "}
                      Salesforce
                    </MobileIntegration>

                    <MobileIntegration to="/integrations/hubspot/">
                      <IntegrationIcon color="#FC7A59">
                        <img
                          src={
                            require("../../assets/images/integrations/logos/hubspot-mark-white.svg")
                              .default
                          }
                          width="20px"
                          alt="HubSpot"
                        />
                      </IntegrationIcon>{" "}
                      HubSpot
                    </MobileIntegration>

                    <MobileIntegration to="/integrations/bold/">
                      <IntegrationIcon color="#E60045">
                        <img
                          src={
                            require("../../assets/images/integrations/logos/bold-mark-white.svg")
                              .default
                          }
                          width="16px"
                          alt="Bold"
                        />
                      </IntegrationIcon>{" "}
                      Bold
                    </MobileIntegration>
                  </MobileMenuSectionGrid>
                  <BlueMobileMenuItem to="/integrations/" tw="!pt-3">
                    <span>
                      See all integrations{" "}
                      <FontAwesomeIcon icon={faArrowRightLong} />
                    </span>
                  </BlueMobileMenuItem>
                </MobileMenuSectionContent>
              </MobileMenuSectionWave>
            </MobileMenuContent>
          </MobileMenuContentWrapper>
          <MobileMenuFooter
            className={classNames(
              isActive && "is-active",
              menuIsScrolled && "is-scrolled",
              menuIsScrollable && "is-scrollable",
              menuIsBottomScrolled && "is-bottom-scrolled"
            )}
          >
            <MobileMenuButtonGroup>
              <Button
                tag="a"
                href="#get-a-demo"
                color="primary"
                className="get-a-demo"
                onClick={(e) => closeMenu()}
              >
                Get a demo
              </Button>
              <Button tag="a" color="white" href="https://app.prosperstack.com">
                Log in
              </Button>
            </MobileMenuButtonGroup>
          </MobileMenuFooter>
        </MobileNav>
      </Nav>
    </>
  );
};

export default HeaderNav;
